"use client";

import { AskSabine } from "@/components/nav/CoachLauncher";
import HelpButton from "@/components/nav/HelpButton";
import { NavPanelShell } from "@/components/nav/NavPanel";
import NavSearchRow from "@/components/nav/NavSearchRow";
import StudioBand from "@/components/nav/StudioBand";
import {
  NAV_ARRIVE,
  NAV_BESIDE_RAIL,
  NAV_COLUMN_WIDTH,
  NAV_INK,
  NAV_TOP_BAR_HEIGHT,
  arriveStep,
} from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

/**
 * The navy bar across the top of a wide screen, above the rail and the page:
 * the studio's mark, the search field, Ask Sabine right beside it, and Help
 * at the far end. It stays put while the page scrolls.
 *
 * While search or Sabine's chat has the column beside the rail, the bar lines
 * up with it: the mark shrinks to the rail's width and what drives the column
 * sits right above it, at its width. For search that is the input (rendered
 * by the pane into `search.inputSlotRef`); for the chat it is her header
 * (rendered by the chat into `chatHeaderSlotRef`), and the field and the
 * launcher make way. Both slots are laid over the column in a NavPanelShell
 * of their own, always mounted, so they come and go with the column below:
 * they fade and narrow as it closes rather than vanishing ahead of it.
 *
 * @param {object} props
 * @param {object} props.studio  StudioBand's props
 * @param {object} props.search  NavSearchRow's props
 * @param {{ placeholder: string, onOpen: () => void } | null} props.ask  The
 *   page's coach; null when the page registered none
 * @param {{ open: boolean, onClick: () => void } | null} props.help
 * @param {boolean} props.chatOpen
 * @param {(node: HTMLElement | null) => void} props.chatHeaderSlotRef
 * @param {boolean} [props.inert]
 */
export default function NavTopBar({
  studio,
  search,
  ask,
  help,
  chatOpen,
  chatHeaderSlotRef,
  inert,
}) {
  const columnOpen = chatOpen || search.open;
  return (
    <header
      inert={inert}
      data-app-nav
      data-app-top-bar
      className={cn(
        "sticky top-0 z-40 hidden shrink-0 items-center md:flex print:hidden",
        NAV_TOP_BAR_HEIGHT,
        NAV_INK,
        columnOpen ? "pr-4" : "gap-3 px-4",
      )}
    >
      <StudioBand {...studio} compact={columnOpen} />
      <NavSearchRow {...search} hidden={chatOpen} />
      {ask && !chatOpen && (
        // Keyed so it comes in again, in its new place, when search opens
        // or closes.
        <div
          key={search.open ? "beside-search" : "beside-field"}
          className={cn(NAV_ARRIVE, "flex min-w-0", search.open && "ml-3")}
          style={arriveStep(1)}
        >
          <AskSabine placeholder={ask.placeholder} onOpen={ask.onOpen} ink />
        </div>
      )}
      <div className={cn("absolute inset-y-0 flex", NAV_BESIDE_RAIL)}>
        <NavPanelShell open={search.open} width={NAV_COLUMN_WIDTH}>
          {/* px-2: the pane's rows sit 8px in from its edges. */}
          <div
            ref={search.inputSlotRef}
            className={cn(NAV_ARRIVE, "my-auto px-2")}
            style={arriveStep(0)}
          />
        </NavPanelShell>
        <NavPanelShell open={chatOpen} width={NAV_COLUMN_WIDTH}>
          <div
            ref={chatHeaderSlotRef}
            className={cn(NAV_ARRIVE, "my-auto")}
            style={arriveStep(0)}
          />
        </NavPanelShell>
      </div>
      {help && (
        <HelpButton
          open={help.open}
          onClick={help.onClick}
          className="ml-auto"
        />
      )}
    </header>
  );
}
